import { PaginationControls } from "@/components/pagination-controls";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { useServerPagination } from "@/hooks/use-pagination";
import { useDebounce } from "@/hooks/useDebounce";
import { useToast } from "@/hooks/use-toast";
import { AuditLogService } from "@/lib/api/audit-log.service";
import { categoryService } from "@/lib/api/categories.service";
import {
  ContentOperationService,
  type DashboardCounts,
  type DashboardPage,
  type DashboardRow,
} from "@/lib/api/content.operation.service";
import { useAuth } from "@/lib/auth";
import { withFromParam } from "@/lib/back-nav";
import { CONTENT_TYPES, getContentTypeApiBase } from "@/lib/content-types";
import { queryClient } from "@/lib/queryClient";
import type { AuditLogEntry, Category } from "@/lib/schema";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  Activity,
  AlertCircle,
  AlertTriangle,
  Archive,
  ArrowRight,
  ArrowUpDown,
  Check,
  CheckCircle2,
  Clock,
  Edit3,
  ExternalLink,
  Eye,
  FileText,
  Filter,
  Globe,
  Plus,
  Search,
  Send,
  ShieldCheck,
  Tags,
  Users,
  XCircle,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "wouter";
import { ISSUE_TYPE_LABELS } from "@/lib/studio-constants";

interface HealthResponse {
  issues: any[];
  total: number;
  issueCounts: Record<string, number>;
}

const contentTypeIcons: Record<string, React.ElementType> = Object.fromEntries(
  Object.entries(CONTENT_TYPES).map(([k, v]) => [k, v.icon]),
);

const contentTypeLabels: Record<string, string> = Object.fromEntries(
  Object.entries(CONTENT_TYPES).map(([k, v]) => [k, v.label]),
);

const statusConfig: Record<
  string,
  {
    label: string;
    variant: "default" | "secondary" | "outline" | "destructive";
    icon: React.ElementType;
    color: string;
  }
> = {
  draft: {
    label: "Draft",
    variant: "secondary",
    icon: Edit3,
    color: "text-muted-foreground",
  },
  submitted: {
    label: "Submitted",
    variant: "default",
    icon: Send,
    color: "text-blue-600 dark:text-blue-400",
  },
  transcoding: {
    label: "Transcoding",
    variant: "outline",
    icon: Clock,
    color: "text-amber-600 dark:text-amber-400",
  },
  published: {
    label: "Published",
    variant: "default",
    icon: Eye,
    color: "text-green-600 dark:text-green-400",
  },
  changes_requested: {
    label: "Changes Requested",
    variant: "destructive",
    icon: AlertTriangle,
    color: "text-red-600 dark:text-red-400",
  },
  failed: {
    label: "Failed",
    variant: "destructive",
    icon: AlertCircle,
    color: "text-red-600 dark:text-red-400",
  },
  archived: {
    label: "Archived",
    variant: "secondary",
    icon: Archive,
    color: "text-muted-foreground",
  },
};

const actionIcons: Record<string, React.ElementType> = {
  content_created: FileText,
  content_updated: FileText,
  content_submitted: Clock,
  content_approved: CheckCircle2,
  content_published: CheckCircle2,
  content_rejected: AlertCircle,
  content_archived: Activity,
};

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function ContentTypeIcon({ type }: { type: string }) {
  const Icon = contentTypeIcons[type] || FileText;
  return <Icon className="h-4 w-4 text-muted-foreground shrink-0" />;
}

function StatusBadge({ status }: { status: string }) {
  const config = statusConfig[status] || {
    label: status,
    variant: "outline" as const,
    color: "",
  };
  return (
    <Badge
      variant={config.variant}
      className="text-xs gap-1"
      data-testid={`badge-status-${status}`}
    >
      {config.label}
    </Badge>
  );
}

function DashboardTab() {
  const { user } = useAuth();
  const isAdmin =
    user?.roles.includes("admin") ||
    user?.roles.includes("super_admin") ||
    user?.roles.includes("coach_manager");
  const isCoach = user?.roles.includes("coach");

  const { data: allContent, isLoading: contentLoading } =
    useQuery<DashboardCounts>({
      queryKey: ["/content/dashboard/summary"],
      // EVO-93: actions on type pages don't touch these keys; refetch on return.
      refetchOnMount: "always",
      queryFn: () => ContentOperationService.getDashboardCounts(),
      enabled: isAdmin || isCoach,
    });

  const { data: healthData, isLoading: healthLoading } =
    useQuery<HealthResponse>({
      queryKey: ["/content/health-issues", "summary"],
      refetchOnMount: "always",
      queryFn: () => ContentOperationService.getHealthIssues({ limit: 0 }),
      enabled: isAdmin,
    });

  const auditFilters = {
    limit: 5,
    entityType: "content",
    ...(isCoach && user?.id ? { actorId: user.id } : {}),
  };

  const { data: recentLogsData, isLoading: logsLoading } = useQuery({
    queryKey: ["/api/audit-logs", auditFilters],
    queryFn: () => AuditLogService.findAll(auditFilters),
  });
  const recentLogs: AuditLogEntry[] = recentLogsData?.entries ?? [];

  const pendingCount = allContent
    ? allContent.statusCounts["submitted"] || 0
    : 0;
  const liveCount = allContent?.statusCounts["published"] || 0;

  const statuses = [
    {
      key: "draft",
      label: "Drafts",
      color: "text-muted-foreground",
      dot: "bg-slate-400",
    },
    {
      key: "submitted",
      label: "Submitted",
      color: "text-blue-600 dark:text-blue-400",
      dot: "bg-blue-500",
    },
    {
      key: "transcoding",
      label: "Transcoding",
      color: "text-amber-600 dark:text-amber-400",
      dot: "bg-amber-500",
    },
    {
      key: "published",
      label: "Published",
      color: "text-green-600 dark:text-green-400",
      dot: "bg-emerald-500",
    },
    {
      key: "changes_requested",
      label: "Changes Requested",
      color: "text-red-600 dark:text-red-400",
      dot: "bg-red-500",
    },
    {
      key: "failed",
      label: "Failed",
      color: "text-red-600 dark:text-red-400",
      dot: "bg-red-500",
    },
    {
      key: "archived",
      label: "Archived",
      color: "text-muted-foreground",
      dot: "bg-slate-400",
    },
  ];

  const topTypes = allContent
    ? Object.entries(allContent.typeStatusCounts || {})
        .map(([type, counts]) => [type, counts["published"] || 0] as const)
        .filter(([, count]) => count > 0)
        .sort(([, a], [, b]) => b - a)
    : [];

  const coachStats = allContent?.authoredCounts ?? {
    submitted: 0,
    live: 0,
    total: 0,
  };

  return (
    <div className="space-y-6" data-testid="tab-content-dashboard">
      {contentLoading ? (
        <Skeleton className="h-24 w-full" />
      ) : allContent ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {statuses.map((s) => (
            <Card key={s.key} data-testid={`card-status-${s.key}`}>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className={`w-2 h-2 rounded-full ${s.dot}`} />
                  <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    {s.label}
                  </span>
                </div>
                <p className={`text-2xl font-semibold tabular-nums ${s.color}`}>
                  {allContent.statusCounts[s.key] || 0}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : null}

      <div className="grid lg:grid-cols-2 gap-4">
        <Card className="flex flex-col" data-testid="card-section-approvals">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between gap-2">
              <CardTitle className="text-base font-serif font-medium flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-blue-500" />
                Approval Center
              </CardTitle>
              {pendingCount > 0 && (
                <Badge variant="default" data-testid="badge-pending-approvals">
                  {pendingCount}
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent className="flex-1 flex flex-col">
            {contentLoading ? (
              <Skeleton className="h-16 w-full" />
            ) : (
              <>
                <div className="space-y-2 mb-3">
                  <div className="flex items-center justify-between gap-4">
                    <span className="text-sm text-muted-foreground">
                      Pending Review
                    </span>
                    <span className="text-sm font-medium tabular-nums">
                      {allContent?.statusCounts["submitted"] || 0}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <span className="text-sm text-muted-foreground">
                      Published
                    </span>
                    <span className="text-sm font-medium tabular-nums">
                      {allContent?.statusCounts["published"] || 0}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <span className="text-sm text-muted-foreground">
                      Changes Requested
                    </span>
                    <span className="text-sm font-medium tabular-nums">
                      {allContent?.statusCounts["changes_requested"] || 0}
                    </span>
                  </div>
                </div>
                <Link
                  href="/studio/review"
                  className="mt-auto"
                  data-testid="link-review-page"
                >
                  <Button variant="outline" size="sm" className="w-full gap-2">
                    Open Approval Center
                    <ArrowRight className="h-3.5 w-3.5" />
                  </Button>
                </Link>
              </>
            )}
          </CardContent>
        </Card>

        <Card className="flex flex-col" data-testid="card-section-live">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between gap-2">
              <CardTitle className="text-base font-serif font-medium flex items-center gap-2">
                <Eye className="h-4 w-4 text-emerald-500" />
                Live Content
              </CardTitle>
              {liveCount > 0 && (
                <Badge variant="secondary" data-testid="badge-live-count">
                  {liveCount}
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent className="flex-1 flex flex-col">
            {contentLoading ? (
              <Skeleton className="h-16 w-full" />
            ) : (
              <>
                <div className="space-y-2 mb-3">
                  {topTypes.map(([type, count]) => {
                    const Icon = contentTypeIcons[type] || FileText;
                    return (
                      <div key={type} className="flex items-center gap-3">
                        <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
                        <span className="text-sm text-muted-foreground capitalize flex-1">
                          {type}
                        </span>
                        <span className="text-sm font-medium tabular-nums">
                          {count}
                        </span>
                      </div>
                    );
                  })}
                  {topTypes.length === 0 && (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      No content yet
                    </p>
                  )}
                </div>
                <Link
                  href="/studio/published"
                  className="mt-auto"
                  data-testid="link-live-page"
                >
                  <Button variant="outline" size="sm" className="w-full gap-2">
                    Open Live Content
                    <ArrowRight className="h-3.5 w-3.5" />
                  </Button>
                </Link>
              </>
            )}
          </CardContent>
        </Card>

        <Card className="flex flex-col" data-testid="card-section-quality">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between gap-2">
              <CardTitle className="text-base font-serif font-medium flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-500" />
                Quality Monitor
              </CardTitle>
              {healthData && healthData.total > 0 && (
                <Badge variant="outline" data-testid="badge-quality-issues">
                  {healthData.total}
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent className="flex-1 flex flex-col">
            {!isAdmin ? (
              <p className="text-sm text-muted-foreground py-2">
                Admin access required
              </p>
            ) : healthLoading ? (
              <Skeleton className="h-16 w-full" />
            ) : healthData ? (
              <>
                {healthData.total === 0 ? (
                  <div className="flex items-center gap-2 text-sm text-emerald-600 dark:text-emerald-400 mb-3">
                    <CheckCircle2 className="h-4 w-4" />
                    All content is healthy
                  </div>
                ) : (
                  <div className="space-y-2 mb-3">
                    {Object.entries(healthData.issueCounts)
                      .slice(0, 3)
                      .map(([issue, count]) => (
                        <div
                          key={issue}
                          className="flex items-center justify-between gap-4"
                        >
                          <span className="text-sm text-muted-foreground">
                            {ISSUE_TYPE_LABELS[issue] ?? issue}
                          </span>
                          <span className="text-sm font-medium tabular-nums text-amber-600 dark:text-amber-400">
                            {count}
                          </span>
                        </div>
                      ))}
                  </div>
                )}
                <Link
                  href="/studio/quality"
                  className="mt-auto"
                  data-testid="link-quality-page"
                >
                  <Button variant="outline" size="sm" className="w-full gap-2">
                    Open Quality Monitor
                    <ArrowRight className="h-3.5 w-3.5" />
                  </Button>
                </Link>
              </>
            ) : null}
          </CardContent>
        </Card>

        {isAdmin && (
          <Card
            className="flex flex-col"
            data-testid="card-section-coach-performance"
          >
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-serif font-medium flex items-center gap-2">
                <Users className="h-4 w-4 text-violet-500" />
                Coach Performance
              </CardTitle>
            </CardHeader>
            <CardContent className="flex-1 flex flex-col">
              {contentLoading ? (
                <Skeleton className="h-16 w-full" />
              ) : allContent ? (
                <>
                  <div className="space-y-2 mb-3">
                    <div className="flex items-center justify-between gap-4">
                      <span className="text-sm text-muted-foreground">
                        Content Submitted
                      </span>
                      <span className="text-sm font-medium tabular-nums">
                        {coachStats.submitted}
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-4">
                      <span className="text-sm text-muted-foreground">
                        Live Content
                      </span>
                      <span className="text-sm font-medium tabular-nums">
                        {coachStats.live}
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-4">
                      <span className="text-sm text-muted-foreground">
                        Total Items
                      </span>
                      <span className="text-sm font-medium tabular-nums">
                        {coachStats.total}
                      </span>
                    </div>
                  </div>
                  <Link
                    href="/coaches"
                    className="mt-auto"
                    data-testid="link-coaches"
                  >
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full gap-2"
                    >
                      View Coaches
                      <ArrowRight className="h-3.5 w-3.5" />
                    </Button>
                  </Link>
                </>
              ) : null}
            </CardContent>
          </Card>
        )}
      </div>

      <Card data-testid="card-section-activity">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-2">
            <CardTitle className="text-base font-serif font-medium flex items-center gap-2">
              <Activity className="h-4 w-4 text-muted-foreground" />
              Recent Activity
            </CardTitle>
            <Link href="/studio/activity" data-testid="link-activity-page">
              <Button
                variant="ghost"
                size="sm"
                className="gap-1 text-muted-foreground"
              >
                View All
                <ArrowRight className="h-3.5 w-3.5" />
              </Button>
            </Link>
          </div>
        </CardHeader>
        <CardContent>
          {logsLoading ? (
            <Skeleton className="h-32 w-full" />
          ) : recentLogs.length > 0 ? (
            <div className="space-y-1">
              {recentLogs.slice(0, 5).map((log) => {
                const ActionIcon = actionIcons[log.actionType] || Activity;
                return (
                  <div
                    key={log.id}
                    className="flex items-start gap-3 p-3 rounded-md hover-elevate"
                    data-testid={`activity-${log.id}`}
                  >
                    <ActionIcon className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm">{log.description}</p>
                      <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                        <span className="text-xs text-muted-foreground">
                          {log.actorUsername || "System"}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {formatRelativeTime(log.timestamp)}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-4">
              No recent activity
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

type SortField = "updatedAt" | "createdAt" | "title" | "contentType" | "status";
type SortDir = "asc" | "desc";

function ManageContentTab() {
  const [location, setLocation] = useLocation();
  const { user } = useAuth();

  const canEditItem = (createdBy: string) => {
    if (!user) return false;
    if (user.roles.includes("coach")) return createdBy === user.id;
    return (
      user.roles.includes("admin") ||
      user.roles.includes("super_admin") ||
      user.roles.includes("coach_manager")
    );
  };

  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearch = useDebounce(searchQuery, 300);
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [ownerFilter, setOwnerFilter] = useState("all");
  const [sortField, setSortField] = useState<SortField>("updatedAt");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [
    typeFilter,
    statusFilter,
    categoryFilter,
    ownerFilter,
    debouncedSearch,
    sortField,
    sortDir,
  ]);

  const { data: countsData, isLoading: countsLoading } =
    useQuery<DashboardCounts>({
      queryKey: ["/content/dashboard/summary"],
      refetchOnMount: "always",
      queryFn: () => ContentOperationService.getDashboardCounts(),
    });

  const { data: categories = [] } = useQuery<Category[]>({
    queryKey: ["categories"],
    queryFn: () => categoryService.findAll(),
  });

  const { data: itemsData, isLoading } = useQuery<DashboardPage>({
    queryKey: [
      "content-manage-items",
      {
        typeFilter,
        statusFilter,
        categoryFilter,
        ownerFilter,
        searchQuery: debouncedSearch,
        sortField,
        sortDir,
        page,
      },
    ],
    refetchOnMount: "always",
    queryFn: () =>
      ContentOperationService.getDashboardItems({
        type: typeFilter,
        status: statusFilter,
        categoryId: categoryFilter,
        ownerId: ownerFilter,
        search: debouncedSearch,
        sortField,
        sortDir,
        page,
        limit: 10,
      }),
  });

  const items = itemsData?.items ?? [];
  const totalItems = itemsData?.total ?? 0;

  const paginationProps = useServerPagination({
    page: itemsData?.page ?? page,
    total: totalItems,
    pageSize: itemsData?.limit ?? 10,
    onPageChange: setPage,
  });

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("desc");
    }
  };

  const clearFilters = () => {
    setSearchQuery("");
    setTypeFilter("all");
    setStatusFilter("all");
    setCategoryFilter("all");
    setOwnerFilter("all");
  };

  const hasActiveFilters =
    typeFilter !== "all" ||
    statusFilter !== "all" ||
    categoryFilter !== "all" ||
    ownerFilter !== "all" ||
    searchQuery.trim() !== "";

  const availableTypes = useMemo(() => {
    if (!countsData?.typeCounts) return [];
    return Object.entries(countsData.typeCounts)
      .filter(([, count]) => count > 0)
      .sort(([a], [b]) => a.localeCompare(b));
  }, [countsData?.typeCounts]);

  const availableOwners = countsData?.owners ?? [];

  const roleLabel = (role: string) => {
    switch (role) {
      case "super_admin":
        return "Super Admin";
      case "admin":
        return "Admin";
      case "coach_manager":
        return "Coach Manager";
      case "coach":
        return "Coach";
      default:
        return role || "Unknown";
    }
  };

  return (
    <div className="space-y-5" data-testid="tab-content-manage">
      {countsLoading ? (
        <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-20" />
          ))}
        </div>
      ) : countsData ? (
        <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
          {Object.entries(statusConfig).map(([key, cfg]) => {
            const count = countsData.statusCounts[key] || 0;
            const isActive = statusFilter === key;
            return (
              <Card
                key={key}
                className={`cursor-pointer transition-colors ${isActive ? "border-primary bg-primary/5" : ""}`}
                onClick={() => setStatusFilter(isActive ? "all" : key)}
                data-testid={`card-filter-status-${key}`}
              >
                <CardContent className="p-3 text-center">
                  <p
                    className={`text-xl font-semibold tabular-nums ${cfg.color}`}
                  >
                    {count}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {cfg.label}
                  </p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : null}

      <Card data-testid="card-filters">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by title, type, category, or creator..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="input-search"
              />
            </div>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger
                className="w-[150px]"
                data-testid="select-type-filter"
              >
                <Filter className="h-3.5 w-3.5 mr-1.5 text-muted-foreground" />
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {availableTypes.map(([type, count]) => (
                  <SelectItem key={type} value={type}>
                    {contentTypeLabels[type] || type} ({count})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger
                className="w-[170px]"
                data-testid="select-category-filter"
              >
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat.id} value={cat.id}>
                    {cat.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={ownerFilter} onValueChange={setOwnerFilter}>
              <SelectTrigger
                className="w-[220px]"
                data-testid="select-owner-filter"
              >
                <SelectValue placeholder="All Owners" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Owners</SelectItem>
                {availableOwners.map((owner) => (
                  <SelectItem
                    key={owner.id}
                    value={owner.id}
                    data-testid={`option-owner-${owner.id}`}
                  >
                    <span className="flex items-center gap-2">
                      <span className="font-medium">{owner.name}</span>
                      <span className="text-xs text-muted-foreground">
                        {roleLabel(owner.role)}
                      </span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                data-testid="button-clear-filters"
              >
                Clear filters
              </Button>
            )}
            <div
              className="ml-auto text-sm text-muted-foreground tabular-nums"
              data-testid="text-result-count"
            >
              {totalItems} {totalItems === 1 ? "item" : "items"}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card data-testid="card-content-table">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b bg-muted/30">
                <th className="text-left p-3">
                  <button
                    className="flex items-center gap-1 text-xs font-medium text-muted-foreground uppercase tracking-wider hover:text-foreground"
                    onClick={() => toggleSort("title")}
                    data-testid="button-sort-title"
                  >
                    Title
                    <ArrowUpDown className="h-3 w-3" />
                  </button>
                </th>
                <th className="text-left p-3">
                  <button
                    className="flex items-center gap-1 text-xs font-medium text-muted-foreground uppercase tracking-wider hover:text-foreground"
                    onClick={() => toggleSort("contentType")}
                    data-testid="button-sort-type"
                  >
                    Type
                    <ArrowUpDown className="h-3 w-3" />
                  </button>
                </th>
                <th className="text-left p-3 hidden md:table-cell">
                  <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Language
                  </span>
                </th>
                <th className="text-left p-3">
                  <button
                    className="flex items-center gap-1 text-xs font-medium text-muted-foreground uppercase tracking-wider hover:text-foreground"
                    onClick={() => toggleSort("status")}
                    data-testid="button-sort-status"
                  >
                    Status
                    <ArrowUpDown className="h-3 w-3" />
                  </button>
                </th>
                <th className="text-left p-3">
                  <button
                    className="flex items-center gap-1 text-xs font-medium text-muted-foreground uppercase tracking-wider hover:text-foreground"
                    onClick={() => toggleSort("updatedAt")}
                    data-testid="button-sort-updated"
                  >
                    Last Updated
                    <ArrowUpDown className="h-3 w-3" />
                  </button>
                </th>
                <th className="text-right p-3">
                  <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Actions
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                Array.from({ length: 8 }).map((_, i) => (
                  <tr key={i} className="border-b">
                    <td colSpan={6} className="p-3">
                      <Skeleton className="h-6 w-full" />
                    </td>
                  </tr>
                ))
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={6} className="p-12 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <FileText className="h-10 w-10 text-muted-foreground/40" />
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">
                          No content found
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {hasActiveFilters
                            ? "Try adjusting your filters or search query"
                            : "Create your first piece of content to get started"}
                        </p>
                      </div>
                      {!hasActiveFilters && (
                        <Link href="/studio">
                          <Button
                            size="sm"
                            className="gap-2 mt-2"
                            data-testid="button-empty-create"
                          >
                            <Plus className="h-3.5 w-3.5" />
                            Create Content
                          </Button>
                        </Link>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                items.map((item: DashboardRow) => (
                  <tr
                    key={`${item.contentType}-${item.id}`}
                    className="border-b hover:bg-muted/30 transition-colors"
                    data-testid={`row-content-${item.id}`}
                  >
                    <td className="p-3">
                      <div className="flex items-center gap-2 min-w-0">
                        <ContentTypeIcon type={item.contentType} />
                        <Link href={withFromParam(item.editUrl, location)}>
                          <span
                            className="text-sm font-medium text-foreground hover:text-primary truncate max-w-[250px] inline-block cursor-pointer"
                            data-testid={`link-title-${item.id}`}
                          >
                            {item.title}
                          </span>
                        </Link>
                      </div>
                    </td>
                    <td className="p-3">
                      <span className="text-sm text-muted-foreground capitalize">
                        {contentTypeLabels[item.contentType] ||
                          item.contentType}
                      </span>
                    </td>
                    <td className="p-3 hidden md:table-cell">
                      <span className="text-sm text-muted-foreground tabular-nums">
                        {item.languageCount}
                      </span>
                    </td>
                    <td className="p-3">
                      <StatusBadge status={item.status} />
                    </td>
                    <td className="p-3">
                      <span className="text-sm text-muted-foreground whitespace-nowrap">
                        {formatRelativeTime(item.updatedAt)}
                      </span>
                    </td>
                    <td className="p-3 text-right">
                      {canEditItem(item.createdBy) ? (
                        <Link href={withFromParam(item.editUrl, location)}>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="gap-1.5"
                            data-testid={`button-edit-${item.id}`}
                          >
                            <ExternalLink className="h-3.5 w-3.5" />
                            Edit
                          </Button>
                        </Link>
                      ) : (
                        <Link href={withFromParam(item.editUrl, location)}>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="gap-1.5 text-muted-foreground"
                            data-testid={`button-view-${item.id}`}
                          >
                            <Eye className="h-3.5 w-3.5" />
                            View
                          </Button>
                        </Link>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {totalItems > 0 && (
          <PaginationControls
            currentPage={paginationProps.currentPage}
            totalPages={paginationProps.totalPages}
            totalItems={paginationProps.totalItems}
            startIndex={paginationProps.startIndex}
            endIndex={paginationProps.endIndex}
            canGoNext={paginationProps.canGoNext}
            canGoPrev={paginationProps.canGoPrev}
            onPageChange={paginationProps.setPage}
            onNext={paginationProps.nextPage}
            onPrev={paginationProps.prevPage}
          />
        )}
      </Card>
    </div>
  );
}

function ApprovalQueueTab() {
  const [location, setLocation] = useLocation();
  const { toast } = useToast();
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectingItem, setRejectingItem] = useState<DashboardRow | null>(null);
  const [rejectionNote, setRejectionNote] = useState("");

  const [page, setPage] = useState(1);

  const { data, isLoading } = useQuery<DashboardPage>({
    queryKey: ["content-approval-items", { page }],
    refetchOnMount: "always",
    queryFn: () =>
      ContentOperationService.getDashboardItems({
        status: "submitted",
        page,
        limit: 10,
      }),
  });

  // Server already returns submitted items oldest-first (FIFO) — do not re-sort.
  const submittedItems = data?.items ?? [];
  const paginationProps = useServerPagination({
    page: data?.page ?? page,
    total: data?.total ?? 0,
    pageSize: data?.limit ?? 10,
    onPageChange: setPage,
  });

  const invalidateAfterLifecycle = (contentType: string) => {
    queryClient.invalidateQueries({
      predicate: (query) => {
        const key = String(query.queryKey[0] || "");
        if (key.startsWith("/content/dashboard")) return true;
        if (key.startsWith("content-manage-")) return true;
        if (key.startsWith("content-approval-")) return true;
        if (key.startsWith("content-review-")) return true;
        if (key.startsWith("content-published-")) return true;
        const apiBase = getContentTypeApiBase(contentType);
        if (apiBase && key.startsWith(apiBase)) return true;
        return false;
      },
    });
  };

  const approveMutation = useMutation({
    mutationFn: async (item: DashboardRow) => {
      await ContentOperationService.reviewContent(item.contentType, item.id, {
        action: "approve",
      });
    },
    onSuccess: (_data, item) => {
      toast({ title: "Content approved successfully" });
      invalidateAfterLifecycle(item.contentType);
    },
    onError: (err: Error) => {
      toast({
        title: "Failed to approve content",
        description: err?.message || undefined,
        variant: "destructive",
      });
    },
  });

  const rejectMutation = useMutation({
    mutationFn: async ({
      item,
      note,
    }: {
      item: DashboardRow;
      note: string;
    }) => {
      await ContentOperationService.reviewContent(item.contentType, item.id, {
        action: "changes_requested",
        note,
      });
    },
    onSuccess: (_data, vars) => {
      toast({ title: "Content rejected" });
      setRejectDialogOpen(false);
      setRejectingItem(null);
      setRejectionNote("");
      invalidateAfterLifecycle(vars.item.contentType);
    },
    onError: (err: Error) => {
      toast({
        title: "Failed to reject content",
        description: err?.message || undefined,
        variant: "destructive",
      });
    },
  });

  const openRejectDialog = (item: DashboardRow) => {
    setRejectingItem(item);
    setRejectionNote("");
    setRejectDialogOpen(true);
  };

  return (
    <div className="space-y-5" data-testid="tab-content-approval">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <p className="text-sm text-muted-foreground">
          Content submitted for review and awaiting approval
        </p>
        <span
          className="text-sm text-muted-foreground tabular-nums"
          data-testid="text-approval-count"
        >
          {submittedItems.length}{" "}
          {submittedItems.length === 1 ? "item" : "items"} pending
        </span>
      </div>

      {isLoading ? (
        <Skeleton className="h-96 w-full" />
      ) : submittedItems.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <div className="flex flex-col items-center gap-3">
              <CheckCircle2 className="h-10 w-10 text-emerald-500/40" />
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  All caught up
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  No content awaiting approval
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="border rounded-md overflow-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[50px]">Type</TableHead>
                <TableHead>Title</TableHead>
                <TableHead className="hidden md:table-cell">Creator</TableHead>
                <TableHead className="hidden md:table-cell">
                  Submitted
                </TableHead>
                <TableHead className="hidden lg:table-cell">
                  Languages
                </TableHead>
                <TableHead className="hidden lg:table-cell">Tags</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {submittedItems.map((item: DashboardRow) => (
                <TableRow
                  key={`${item.contentType}-${item.id}`}
                  data-testid={`row-approval-${item.id}`}
                >
                  <TableCell>
                    <ContentTypeIcon type={item.contentType} />
                  </TableCell>
                  <TableCell
                    className="font-medium max-w-[200px] truncate"
                    data-testid={`text-approval-title-${item.id}`}
                  >
                    {item.title}
                  </TableCell>
                  <TableCell
                    className="hidden md:table-cell text-sm text-muted-foreground"
                    data-testid={`text-approval-creator-${item.id}`}
                  >
                    {item.createdByName || "Unknown"}
                  </TableCell>
                  <TableCell
                    className="hidden md:table-cell text-sm text-muted-foreground whitespace-nowrap"
                    data-testid={`text-approval-date-${item.id}`}
                  >
                    {item.submittedAt
                      ? formatRelativeTime(item.submittedAt)
                      : formatRelativeTime(item.updatedAt)}
                  </TableCell>
                  <TableCell className="hidden lg:table-cell">
                    <div className="flex items-center gap-1">
                      <Globe className="h-3 w-3 text-muted-foreground" />
                      <span className="text-sm tabular-nums">
                        {item.languageCount}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="hidden lg:table-cell">
                    <div className="flex items-center gap-1">
                      <Tags className="h-3 w-3 text-muted-foreground" />
                      <span className="text-sm tabular-nums">
                        {item.tagCount}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="gap-1"
                        onClick={() => approveMutation.mutate(item)}
                        disabled={approveMutation.isPending}
                        data-testid={`button-approve-${item.id}`}
                      >
                        <Check className="h-3.5 w-3.5 text-emerald-600" />
                        Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="gap-1"
                        onClick={() => openRejectDialog(item)}
                        disabled={rejectMutation.isPending}
                        data-testid={`button-reject-${item.id}`}
                      >
                        <XCircle className="h-3.5 w-3.5 text-destructive" />
                        Reject
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() =>
                          setLocation(withFromParam(item.editUrl, location))
                        }
                        data-testid={`button-open-${item.id}`}
                      >
                        <ExternalLink className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <PaginationControls
            currentPage={paginationProps.currentPage}
            totalPages={paginationProps.totalPages}
            totalItems={paginationProps.totalItems}
            startIndex={paginationProps.startIndex}
            endIndex={paginationProps.endIndex}
            canGoNext={paginationProps.canGoNext}
            canGoPrev={paginationProps.canGoPrev}
            onPageChange={paginationProps.setPage}
            onNext={paginationProps.nextPage}
            onPrev={paginationProps.prevPage}
          />
        </div>
      )}

      <Dialog
        open={rejectDialogOpen}
        onOpenChange={(open) => {
          setRejectDialogOpen(open);
          if (!open) {
            setRejectingItem(null);
            setRejectionNote("");
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Content</DialogTitle>
            <DialogDescription>
              Provide a reason for rejecting "{rejectingItem?.title}". The
              creator will be notified.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            placeholder="Rejection reason..."
            value={rejectionNote}
            onChange={(e) => setRejectionNote(e.target.value)}
            className="min-h-[100px]"
            data-testid="textarea-rejection-note"
          />
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRejectDialogOpen(false)}
              data-testid="button-cancel-reject"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (rejectingItem && rejectionNote.trim()) {
                  rejectMutation.mutate({
                    item: rejectingItem,
                    note: rejectionNote,
                  });
                }
              }}
              disabled={!rejectionNote.trim() || rejectMutation.isPending}
              data-testid="button-confirm-reject"
            >
              Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function ContentOperationsPage() {
  const { user } = useAuth();
  const isAdmin =
    user?.roles.includes("admin") || user?.roles.includes("super_admin");
  const isCoach = user?.roles.includes("coach");
  const [activeTab, setActiveTab] = useState("dashboard");

  return (
    <div
      className="p-6 space-y-6 max-w-7xl mx-auto"
      data-testid="content-operations-page"
    >
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <h1
            className="text-2xl font-semibold text-foreground"
            style={{ fontFamily: "'Playfair Display', serif" }}
            data-testid="text-page-title"
          >
            Content Operations
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            {isCoach
              ? "Track your drafts, submissions, approvals, and live content"
              : "Dashboard, content management, and approval workflows"}
          </p>
        </div>
        <Link href="/studio">
          <Button className="gap-2" data-testid="button-create-new">
            <Plus className="h-4 w-4" />
            Create New
          </Button>
        </Link>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList data-testid="tabs-operations">
          <TabsTrigger value="dashboard" data-testid="tab-dashboard">
            Dashboard
          </TabsTrigger>
          <TabsTrigger value="manage" data-testid="tab-manage">
            Manage Content
          </TabsTrigger>
          {isAdmin && (
            <TabsTrigger value="approval" data-testid="tab-approval">
              Approval Queue
            </TabsTrigger>
          )}
        </TabsList>
        <TabsContent value="dashboard" className="mt-6">
          <DashboardTab />
        </TabsContent>
        <TabsContent value="manage" className="mt-6">
          <ManageContentTab />
        </TabsContent>
        {isAdmin && (
          <TabsContent value="approval" className="mt-6">
            <ApprovalQueueTab />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
